import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Wheat, AlertOctagon, FlaskConical, ChevronRight, Sparkles } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { api } from "@/lib/api";

const CARDS = [
  {
    to: "/omavalvonta/hygiene",
    label: "Hygiene",
    icon: ShieldCheck,
    accent: "#1F4D3A",
    description: "Per-batch food-safety records: surfaces, equipment, personal hygiene, hand washing, workspace.",
    testid: "oma-card-hygiene",
    countKey: "hygiene",
  },
  {
    to: "/omavalvonta/raw-material",
    label: "Raw Material",
    icon: Wheat,
    accent: "#E85D04",
    description: "Incoming ingredients with temperature control, packaging, visual quality, expiry & batch link.",
    testid: "oma-card-raw-material",
    countKey: "raw",
  },
  {
    to: "/omavalvonta/incidents",
    label: "Incidents",
    icon: AlertOctagon,
    accent: "#D62828",
    description: "Log any deviation with severity, action taken, product status and recall capability.",
    testid: "oma-card-incidents",
    countKey: "incidents",
  },
  {
    to: "/omavalvonta/lab-tests",
    label: "Lab Tests",
    icon: FlaskConical,
    accent: "#2A9D8F",
    description: "OK / Not OK results per product or batch. Simple traceable log.",
    testid: "oma-card-lab-tests",
    countKey: "lab",
  },
];

export default function OmavalvontaHome() {
  const navigate = useNavigate();
  const [counts, setCounts] = useState({ hygiene: 0, raw: 0, incidents: 0, lab: 0 });

  useEffect(() => {
    (async () => {
      try {
        const [h, r, i, l] = await Promise.all([
          api.listHygieneChecks(),
          api.listRawMaterialChecks(),
          api.listIncidents(),
          api.listLabTests(),
        ]);
        setCounts({ hygiene: h.length, raw: r.length, incidents: i.length, lab: l.length });
      } catch {
        /* ignore */
      }
    })();
  }, []);

  return (
    <div data-testid="page-omavalvonta">
      <PageHeader
        overline="Food Safety"
        title="Omavalvonta"
        subtitle="Self-monitoring records — inspection-ready, batch-linked, professional."
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {CARDS.map(({ to, label, icon: Icon, accent, description, testid, countKey }) => (
          <button
            key={to}
            type="button"
            onClick={() => navigate(to)}
            data-testid={testid}
            className="card-soft card-soft-hover text-left p-6 flex items-start gap-4 group"
          >
            <div
              className="w-14 h-14 rounded-xl flex items-center justify-center shrink-0"
              style={{ backgroundColor: `${accent}1f`, color: accent }}
            >
              <Icon size={28} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <h3 className="font-display font-bold text-xl text-[#222]">{label}</h3>
                <ChevronRight size={18} className="text-[#6C757D] group-hover:text-[#E85D04] transition-colors" />
              </div>
              <p className="text-sm text-[#6C757D] mt-1">{description}</p>
              <div className="mt-3 flex items-center gap-2">
                <span
                  className="text-[10px] uppercase tracking-[0.2em] font-bold"
                  style={{ color: accent }}
                >
                  Records
                </span>
                <span className="font-display font-bold text-lg text-[#222]">
                  {counts[countKey]}
                </span>
              </div>
            </div>
          </button>
        ))}
      </div>

      <div className="mt-8 card-soft p-5 flex items-start gap-3">
        <Sparkles size={18} className="text-[#E85D04] shrink-0 mt-0.5" />
        <div className="text-sm text-[#222]">
          <div className="font-semibold text-[#1F4D3A] mb-1">Batch-linked traceability</div>
          <div className="text-[#6C757D]">
            All Omavalvonta records connect back to production batches. Combined with{" "}
            <button
              onClick={() => navigate("/traceability")}
              className="text-[#E85D04] font-semibold hover:underline"
              data-testid="oma-link-traceability"
            >
              Traceability
            </button>{" "}
            and the automatic recall system, you can answer any inspector question in under a minute.
          </div>
        </div>
      </div>
    </div>
  );
}
